import React from 'react';
import { Scissors, Bath, Baby, Hand, ArrowRight } from 'lucide-react';
import './Services.css';

function Services() {
  return (
    <>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-hero__bg">
          <img src="/images/grooming-service.jpg" alt="Dog grooming" />
          <div className="page-hero__overlay" />
        </div>
        <div className="page-hero__content container">
          <span className="section-label" style={{ color: 'var(--color-accent-light)' }}>Our Services</span>
          <h1 className="page-hero__title">Dog Grooming</h1>
          <p className="page-hero__subtitle">
            At Tails &amp; Tweed, dog grooming is approached with skill, patience and a deep
            understanding of coat, breed and behaviour.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="services-intro section">
        <div className="container">
          <div className="services-intro__content">
            <p>
              We are City &amp; Guild qualified professionals, with an International Certified
              Master Groomer on staff, with years of hands-on industry experience, offering
              technically precise grooming delivered with genuine care.
            </p>
            <p>
              We specialise in breed-standard styling, modern trims, and are recognised for our
              expertise in traditional handstripping for both wire coats and gundog breeds.
            </p>
            <p>
              Our salon is calm, structured and welfare focused. Every dog is groomed with time,
              attention and respect.
            </p>
            <a
              href="https://app.toplinedogs.co.uk/public/online-bookings?salon=YWJmZDE4NjAtMWMzZi00NjMyLTkyNTQtMDhkOWI2OGFiZGUx"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-accent"
              style={{ marginTop: 24 }}
            >
              Book Dog Grooming Appointment Now
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="services__list">
            <div className="service-detail">
              <div className="service-detail__image">
                <img src="/images/grooming-service.jpg" alt="Full groom service" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Scissors size={28} />
                </div>
                <h2 className="service-detail__title">Full Grooming</h2>
                <p className="service-detail__subtitle">For those pets whose coats require trimming</p>
                <p className="service-detail__text">
                  Our full groom is a complete service tailored to your dog&rsquo;s coat and lifestyle.
                </p>
                <ul className="service-detail__features">
                  <li>Consultation</li>
                  <li>Luxury bathing in coat specific shampoo</li>
                  <li>Blow drying &amp; coat preparation</li>
                  <li>Nail trimming</li>
                  <li>Ear cleaning</li>
                  <li>Styling of your pet&rsquo;s coat to breed standard or your own requirements with clippers and scissors</li>
                  <li>Spritz of fragrance</li>
                </ul>
                <p className="service-detail__note">
                  Suitable for maintenance grooms, breed specific styling and dogs on a regular schedule.
                </p>
              </div>
            </div>

            <div className="service-detail service-detail--reverse">
              <div className="service-detail__image">
                <img src="/images/corgi-bath.jpg" alt="Bath and brush service" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Bath size={28} />
                </div>
                <h2 className="service-detail__title">Bath and Brush</h2>
                <p className="service-detail__subtitle">
                  For pets who do not require trimming, or as a &lsquo;freshen up&rsquo; in between full grooming
                </p>
                <p className="service-detail__text">
                  For in-between maintenance or short coated breeds.
                </p>
                <ul className="service-detail__features">
                  <li>Thorough cleansing with coat specific shampoo</li>
                  <li>Blow dry</li>
                  <li>De-shedding and brushing out</li>
                  <li>Finishing fragrance</li>
                </ul>
                <p className="service-detail__note">
                  Perfect for maintaining coat condition, reducing shedding, and keeping your dog
                  fresh between full grooms.
                </p>
              </div>
            </div>

            <div className="service-detail">
              <div className="service-detail__image">
                <img src="/images/dog-puppy.jpg" alt="Puppy grooming" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Baby size={28} />
                </div>
                <h2 className="service-detail__title">Puppy Grooming</h2>
                <p className="service-detail__subtitle">
                  Available from 14 weeks to give your puppy the best start
                </p>
                <p className="service-detail__text">
                  Early positive experiences shape a dog&rsquo;s lifelong relationship with grooming.
                  Our puppy sessions focus on:
                </p>
                <ul className="service-detail__features">
                  <li>Calm introductions to equipment</li>
                  <li>Gentle bathing and drying</li>
                  <li>Light face, feet and hygiene trim</li>
                  <li>Building confidence</li>
                </ul>
                <p className="service-detail__note">
                  Ideal from second vaccination completion onwards.
                </p>
              </div>
            </div>

            <div className="service-detail service-detail--reverse">
              <div className="service-detail__image">
                <img src="/images/gundog.jpg" alt="Handstripping" />
              </div>
              <div className="service-detail__content">
                <div className="service-detail__icon">
                  <Hand size={28} />
                </div>
                <h2 className="service-detail__title">Handstripping</h2>
                <p className="service-detail__subtitle">
                  Traditional, specialist grooming of wire coated and gundog breeds
                </p>
                <p className="service-detail__text">
                  We are experienced hand stripping specialists for both wire and gundog breeds.
                  Handstripping:
                </p>
                <ul className="service-detail__features">
                  <li>Preserves correct coat texture</li>
                  <li>Maintains coat colour</li>
                  <li>Supports healthy regrowth</li>
                  <li>Is perfect for many terriers and gundog breeds</li>
                </ul>
                <p className="service-detail__note">
                  This service requires coat condition assessment and regular maintenance.
                  We will advise honestly if your dog&rsquo;s coat is suitable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="page-cta">
        <div className="container">
          <div className="page-cta__inner">
            <div className="page-cta__content">
              <h2>Ready to Book Your Dog&rsquo;s Groom?</h2>
              <p>Get in touch to discuss your dog&rsquo;s needs and book an appointment.</p>
            </div>
            <div className="page-cta__actions">
              <a
                href="https://app.toplinedogs.co.uk/public/online-bookings?salon=YWJmZDE4NjAtMWMzZi00NjMyLTkyNTQtMDhkOWI2OGFiZGUx"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-accent"
              >
                Book Now <ArrowRight size={16} />
              </a>
              <a href="tel:07904813567" className="btn btn-outline" style={{ borderColor: 'var(--color-white)', color: 'var(--color-white)' }}>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
